/**
 * Funcion que obtiene los codigos de los marketplaces seleccionados en el modal de filtros
 * @param {Array} mkp_integration Arreglo con los datos de los marketplaces seleccionados en el modal de filtros
 * @returns {Array} Arreglo con los codigos de los marketplaces seleccionados
 */
function getMkpsCombination(mkp_integration) {
  return mkp_integration.map(mkp => mkp.code)
}
/**
 * VAlida que los marketplaces seleccionados en el modal de filtros sean validos
 * @param {Array} marketplacesArray Arreglo con los marketplaces seleccionados en el modal de filtros
 * @returns {Object} Objeto con la propiedad valid que indica si la combinacion de marketplaces es valida o no
 */
export function setValidMarketplaces(marketplacesArray) {
  // Si esta vacio
  if (marketplacesArray.length === 0) return { valid: false, combination: ['empty'] }

  const selectedMkps = getMkpsCombination(marketplacesArray)
  // Si se selecciona solo uno siempre sera valido
  if (marketplacesArray.length === 1) return { valid: true, combination: selectedMkps }
  // Cuando se seleccione 'ML' y cualquier otro
  if (selectedMkps.length > 1 && selectedMkps.includes('ML')) return { valid: false, combination: ['notValid'] }
  // Cuando se seleccione 'FLB' y 'RPL'
  if (selectedMkps.length === 2 && selectedMkps.includes('RPL') && selectedMkps.includes('FLB')) return { valid: true, combination: selectedMkps }
  // Cualquier combinación extraña sera invalida
  return { valid: false, combination: ['notValid'] }
}
/**
 * Funcion que valida si el marketplace seleccionado en el modal de filtros tiene modos de envio
 * @param {Array} shippingModeArray Arreglo con los modos de entrega seleccionados en el modal de filtros
 * @returns {boolean} Indica si el mkp seleccionado no tiene shipping_mode ni logistic_type
 */
export function isShippingModeEmpty(shippingModeArray) {
  return (
    Array.isArray(shippingModeArray) &&
    shippingModeArray.length === 0
  )
}

/**
 * Funcion que valida si el marketplace seleccionado en el modal de filtros NO tiene modo de envio pero si logistic_type.
 * En este caso llega un solo objeto en shipping_mode que contiene el 'code' en 'default'
 * @param {Array} shippingModeArray Arreglo con los modos de entrega seleccionados en el modal de filtros
 * @returns {boolean} Indica si el mkp seleccionado no tiene deliver_mode y solo tiene logitic_type 'EnviaFlex'
 */
export function isOnlyDefaultShippingMode(shippingModeArray) {
  return (
    Array.isArray(shippingModeArray) &&
    shippingModeArray.length === 1 &&
    shippingModeArray[0].code === 'default'
  )
}

/**
 * Detecta si el marketplace seleccionado en el modal de filtros tiene modos de entrega y tipos de logistica
 * @param {Array} shippingModeArray Arreglo con los modos de entrega seleccionados en el modal de filtros
 * @returns {boolean} Indica si el mkp seleccionado tiene shipping_mode y logistic_type
 */
export function haveShippingModeAndLogisticType(shippingModeArray) {
  return (
    Array.isArray(shippingModeArray) &&
    shippingModeArray.length > 0 &&
    shippingModeArray.some((shippingMode) => {
      return (
        Array.isArray(shippingMode.logistics) &&
        shippingMode.logistics.length > 0
      )
    })
  )
}

/**
 * Verifica si la combinacion de mkp_integration, shipping_mode, logistic_type corresponde a MeliFlex
 * @param {Array} mkp_integration Arreglo con la información de los marketplace seleccionados
 * @param {Object} shipping_mode Modo de envio seleccionado en el formulario de filtros
 * @param {Object} logistic_type Tipo de logistica seleccionado en el formulario de filtros
 * @returns {boolean} Indica si la combinacion de mkp_integration, shipping_mode, logistic_type corresponde a MeliFlex
 */
export function isMeliFlex(mkp_integration, shipping_mode, logistic_type) {
  const selectedMkps = getMkpsCombination(mkp_integration)
  return (
    selectedMkps.includes('ML') &&
    shipping_mode?.id === 'me2' &&
    logistic_type?.id === 'self_service'
  )
}

/**
 * Verifica si la combinacion de mkp_integration, shipping_mode, logistic_type corresponde a EnviaFlex
 * @param {Array} mkp_integration Arreglo con la información de los marketplace seleccionados
 * @param {Object} shipping_mode Modo de envio seleccionado en el formulario de filtros
 * @param {Object} logistic_type Tipo de logistica seleccionado en el formulario de filtros
 * @returns {boolean} Indica si la combinacion de mkp_integration, shipping_mode, logistic_type corresponde a EnviaFlex
 */
export function isEnviaFlex(mkp_integration, shipping_mode, logistic_type) {
  const selectedMkps = getMkpsCombination(mkp_integration)
  const hasRplOrFlb = selectedMkps.includes('RPL') || selectedMkps.includes('FLB')
  return (
    hasRplOrFlb &&
    !shipping_mode &&
    logistic_type?.id === 'enviaflex'
  )
}

/**
 * Verifica si la combinacion de mkp_integration, shipping_mode, logistic_type corresponde a MercadoEnvios 1
 * @param {Array} mkp_integration Arreglo con la información de los marketplace seleccionados
 * @param {Object} shipping_mode Modo de envio seleccionado en el formulario de filtros
 * @param {Object} logistic_type Tipo de logistica seleccionado en el formulario de filtros
 * @returns {boolean} Indica si la combinacion de mkp_integration, shipping_mode, logistic_type corresponde a MercadoEnvios 1
 */
export function isMe1(mkp_integration, shipping_mode, logistic_type) {
  const selectedMkps = getMkpsCombination(mkp_integration)
  return (
    selectedMkps.includes('ML') &&
    shipping_mode?.id === 'me1' &&
    logistic_type?.id === 'default'
  )
}

/**
 * Verifica si se cuimple alguno de los filtros rapidos (MeliFlex, EnviaFlex, MercadoEnvios 1)
 * @param {Array} mkp_integration Arreglo con la información de los marketplace seleccionados
 * @param {Object} shipping_mode Modo de envio seleccionado en el formulario de filtros
 * @param {Object} logistic_type Tipo de logistica seleccionado en el formulario de filtros
 * @returns {Object} Indica si se cumple alguno de los filtros rapidos y el tipo de filtro
 */
export function isQuickFilter(mkp_integration, shipping_mode, logistic_type) {
  const validators = [
    { fn: isMeliFlex, type: 'meli_flex' },
    { fn: isEnviaFlex, type: 'envia_flex' },
    { fn: isMe1, type: 'me_1' }
  ]

  for (const validator of validators) {
    const { fn, type } = validator
    if (fn(mkp_integration, shipping_mode, logistic_type)) {
      return { isQuickFilter: true, type }
    }
  }

  return { isQuickFilter: false, type: null }
}

export function showQuickFilterInput(response) {
  const validators = {
    meli_flex: { showInForm: false },
    envia_flex: { showInForm: false },
    me_1: { showInForm: false }
  }

  // Aca existe un array de shipping_mode que contiene obj y dentro de cada uno de esos un array de logistics
  // debo validar cada una de estas estructuras anidadas para saber si setear el showInForm en true, complicao
  for (const mkp of response) {
    if (mkp.code === 'ML') {
      const shippingModes = mkp.shipping_mode || []

      // Check MELI Flex (me2 + self_service)
      const hasMeliFlex = shippingModes.some(mode =>
        mode.code === 'me2' &&
        (mode.logistics || []).some(logistic => logistic.code === 'self_service')
      )
      if (hasMeliFlex) validators.meli_flex.showInForm = true

      // Check ME1 (me1 + default)
      const hasMe1 = shippingModes.some(mode =>
        mode.code === 'me1' &&
        (mode.logistics || []).some(logistic => logistic.code === 'default')
      )
      if (hasMe1) validators.me_1.showInForm = true
    }

    if (mkp.code === 'RPL' || mkp.code === 'FLB') {
      const hasEnviaFlex = mkp.shipping_mode.some(mode =>
        mode.code === 'default' &&
        (mode.logistics || []).some(logistic => logistic.code === 'enviaflex')
      )
      if (hasEnviaFlex) validators.envia_flex.showInForm = true
    }

    // si encontro todo se corta el loop
    if (validators.meli_flex.showInForm && validators.me_1.showInForm && validators.envia_flex.showInForm) {
      break
    }
  }
  return validators
}

export const fakeData = [
  {
    'code': 'ML',
    'shipping_mode': [
      {
        'code': 'me1',
        'logistics': [
          {
            'code': 'default',
            'name': 'Mercado Default'
          }
        ],
        'name': 'Mercado Envíos 1 [m1]'
      },
      {
        'code': 'me2',
        'logistics': [
          {
            'code': 'cross_docking',
            'name': 'Mercado Envíos Colecta'
          },
          {
            'code': 'xd_drop_off',
            'name': 'Mercado Envíos XD'
          },
          {
            'code': 'self_service',
            'name': 'Mercado Envíos Flex'
          },
          {
            'code': 'fulfillment',
            'name': 'Mercado Envíos Full'
          },
          {
            'code': 'drop_off',
            'name': 'Mercado Envíos'
          }
        ],
        'name': 'Mercado Envíos 2 [m2]'
      },
      {
        'code': 'custom',
        'logistics': [],
        'name': 'Mercado Envíos Custom [custom]'
      }
    ],
    'id': 1,
    'name': 'Mercado Libre'
  },
  {
    'code': 'DAF',
    'shipping_mode': [],
    'id': 2,
    'name': 'Dafiti'
  },
  {
    'code': 'PRM',
    'shipping_mode': [],
    'id': 3,
    'name': 'Paris Marketcenter'
  },
  {
    'code': 'RPL',
    'shipping_mode': [
      {
        'code': 'default',
        'logistics': [
          {
            'code': 'enviaflex',
            'name': 'EnviaFlex'
          }
        ],
        'name': 'Default'
      }
    ],
    'id': 13,
    'name': 'Ripley'
  },
  {
    'code': 'FLB',
    'shipping_mode': [
      {
        'code': 'default',
        'logistics': [
          {
            'code': 'enviaflex',
            'name': 'EnviaFlex'
          }
        ],
        'name': 'Default'
      }
    ],
    'id': 16,
    'name': 'Falabella'
  },
  {
    'code': 'WM',
    'shipping_mode': [],
    'id': 23,
    'name': 'Walmart'
  },
  {
    'code': 'HTS',
    'shipping_mode': [],
    'id': 24,
    'name': 'Hites'
  },
  {
    'code': 'KC',
    'shipping_mode': [],
    'id': 25,
    'name': 'Kitchen Center'
  },
  {
    'code': 'MV',
    'shipping_mode': [],
    'id': 27,
    'name': 'Multivende'
  }
]